import { Link } from 'react-router-dom';
import ServicesSubMenu from '../ServicesSubMenu';
import robotImage from '../../assets/images/robot1.webp';
import '../ServicesWebsites/index.css';

const outcomes = [
  { value: '24/7', label: 'Instant customer answers' },
  { value: '70%', label: 'Potential support labor reduction' },
  { value: '90%', label: 'Common FAQ coverage goal' },
];

const features = [
  'Website chatbot trained on your business information, FAQs, policies, and service details.',
  'SMS support so customers can text common questions and receive useful replies quickly.',
  'Human handoff for questions that need staff review, judgment, or follow-up.',
  'Admin-friendly review of unanswered questions so your knowledge base improves over time.',
  'AWS-hosted architecture designed for reliability, security, monitoring, and scale.',
];

const workflow = [
  {
    title: 'Load Your Knowledge',
    description:
      'Start with your existing FAQs, website pages, service descriptions, policies, and customer support notes.',
  },
  {
    title: 'Customers Ask',
    description:
      'Visitors ask questions through your website chatbot or by SMS when text messaging is part of the setup.',
  },
  {
    title: 'AI Finds the Answer',
    description:
      'The system searches your business knowledge base and drafts a practical answer in seconds.',
  },
  {
    title: 'Your Team Stays in Control',
    description:
      'Complex questions can be escalated, and new unanswered questions become candidates for future FAQ updates.',
  },
];

const useCases = [
  {
    title: 'E-Commerce and Retail',
    description:
      'Answer questions about orders, returns, product details, availability, store hours, and next steps before a customer abandons the purchase.',
  },
  {
    title: 'Local Services',
    description:
      'Help customers quickly understand pricing, availability, service areas, scheduling options, and how to request a quote.',
  },
  {
    title: 'Professional Services',
    description:
      'Handle appointment basics, location details, intake instructions, insurance notes, and common account questions without slowing down your staff.',
  },
  {
    title: 'Software and Membership Sites',
    description:
      'Reduce repetitive onboarding, billing, login, and feature questions while routing unusual issues to the right person.',
  },
];

const ArchitectureDiagram = () => (
  <div className="chatbot-architecture-diagram-card" aria-label="SMS and web chatbot system architecture diagram">
    <svg
      className="chatbot-architecture-diagram"
      viewBox="0 0 1400 700"
      role="img"
      aria-labelledby="chatbot-architecture-svg-title chatbot-architecture-svg-desc"
    >
      <title id="chatbot-architecture-svg-title">System Architecture: SMS and Web Chatbot</title>
      <desc id="chatbot-architecture-svg-desc">
        A system diagram showing Twilio, AWS Lambda, AWS storage and AI services,
        and web CDN components connected by SMS, web chatbot, and internal cloud flows.
      </desc>
      <defs>
        <marker id="chatbot-arrow-gold" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#b8874f" />
        </marker>
        <marker id="chatbot-arrow-blue" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#1f2937" />
        </marker>
      </defs>

      <text x="700" y="34" fontSize="24" fontWeight="800" fill="#1f2937" textAnchor="middle">
        System Architecture: SMS and Web Chatbot
      </text>

      <rect x="30" y="100" width="200" height="550" fill="#f8fafc" stroke="#1a6b7f" strokeWidth="2" rx="8" />
      <text x="130" y="140" fontSize="18" fontWeight="800" fill="#1a6b7f" textAnchor="middle">Twilio</text>
      <rect x="50" y="170" width="160" height="80" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="130" y="200" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">SMS Service</text>
      <text x="130" y="222" fontSize="11" fill="#4b5563" textAnchor="middle">Incoming/Outgoing</text>
      <text x="130" y="240" fontSize="11" fill="#4b5563" textAnchor="middle">Messages</text>
      <rect x="50" y="280" width="160" height="80" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="130" y="310" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Webhook</text>
      <text x="130" y="332" fontSize="11" fill="#4b5563" textAnchor="middle">Trigger on</text>
      <text x="130" y="350" fontSize="11" fill="#4b5563" textAnchor="middle">Message Received</text>
      <rect x="50" y="390" width="160" height="80" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="130" y="420" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Compliance</text>
      <text x="130" y="442" fontSize="11" fill="#4b5563" textAnchor="middle">STOP/OPTOUT</text>
      <text x="130" y="460" fontSize="11" fill="#4b5563" textAnchor="middle">Handling</text>

      <rect x="350" y="100" width="200" height="550" fill="#f8fafc" stroke="#1f2937" strokeWidth="2" rx="8" />
      <text x="450" y="140" fontSize="18" fontWeight="800" fill="#1f2937" textAnchor="middle">AWS Lambda</text>
      <rect x="370" y="170" width="160" height="80" fill="#ffffff" stroke="#1f2937" strokeWidth="2" rx="6" />
      <text x="450" y="195" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Webhook Handler</text>
      <text x="450" y="217" fontSize="11" fill="#4b5563" textAnchor="middle">Receives SMS</text>
      <text x="450" y="235" fontSize="11" fill="#4b5563" textAnchor="middle">from Twilio</text>
      <rect x="370" y="280" width="160" height="80" fill="#ffffff" stroke="#1f2937" strokeWidth="2" rx="6" />
      <text x="450" y="305" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">RAG Query</text>
      <text x="450" y="327" fontSize="11" fill="#4b5563" textAnchor="middle">Process and AI</text>
      <text x="450" y="345" fontSize="11" fill="#4b5563" textAnchor="middle">Inference</text>
      <rect x="370" y="390" width="160" height="80" fill="#ffffff" stroke="#1f2937" strokeWidth="2" rx="6" />
      <text x="450" y="415" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Outbound Reply</text>
      <text x="450" y="437" fontSize="11" fill="#4b5563" textAnchor="middle">Function URL</text>
      <text x="450" y="455" fontSize="11" fill="#4b5563" textAnchor="middle">to Twilio</text>

      <rect x="670" y="100" width="200" height="550" fill="#f8fafc" stroke="#1a6b7f" strokeWidth="2" rx="8" />
      <text x="770" y="140" fontSize="18" fontWeight="800" fill="#1a6b7f" textAnchor="middle">AWS Storage and</text>
      <text x="770" y="165" fontSize="18" fontWeight="800" fill="#1a6b7f" textAnchor="middle">Data Layer</text>
      <rect x="690" y="190" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="220" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">DynamoDB</text>
      <text x="770" y="247" fontSize="10" fill="#4b5563" textAnchor="middle">User Registrations</text>
      <rect x="690" y="280" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="310" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">S3 Vectors</text>
      <text x="770" y="337" fontSize="10" fill="#4b5563" textAnchor="middle">FAQ Vector DB</text>
      <rect x="690" y="370" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="400" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Bedrock</text>
      <text x="770" y="427" fontSize="10" fill="#4b5563" textAnchor="middle">LLM Inference</text>
      <rect x="690" y="460" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="490" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Secrets Manager</text>
      <text x="770" y="517" fontSize="10" fill="#4b5563" textAnchor="middle">API Keys and Tokens</text>

      <rect x="990" y="100" width="200" height="550" fill="#f8fafc" stroke="#b8874f" strokeWidth="2" rx="8" />
      <text x="1090" y="140" fontSize="18" fontWeight="800" fill="#b8874f" textAnchor="middle">Web and CDN</text>
      <rect x="1010" y="190" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="220" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">S3</text>
      <text x="1090" y="247" fontSize="10" fill="#4b5563" textAnchor="middle">Static Website</text>
      <rect x="1010" y="280" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="310" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">CloudFront</text>
      <text x="1090" y="337" fontSize="10" fill="#4b5563" textAnchor="middle">Global CDN Cache</text>
      <rect x="1010" y="370" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="400" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Admin Panel</text>
      <text x="1090" y="427" fontSize="10" fill="#4b5563" textAnchor="middle">User Management</text>
      <rect x="1010" y="460" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="490" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Users</text>
      <text x="1090" y="517" fontSize="10" fill="#4b5563" textAnchor="middle">Web Chatbot Access</text>

      <path d="M 250 210 L 330 210" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="290" y="200" fontSize="10" fill="#b8874f" fontWeight="700">SMS Webhook</text>
      <path d="M 530 200 L 650 220" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" />
      <text x="570" y="190" fontSize="10" fill="#1f2937" fontWeight="700">Query/Update User</text>
      <path d="M 530 315 L 650 315" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" />
      <text x="560" y="305" fontSize="10" fill="#1f2937" fontWeight="700">Search FAQs</text>
      <path d="M 530 400 L 650 400" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" />
      <text x="560" y="390" fontSize="10" fill="#1f2937" fontWeight="700">AI Response</text>
      <path d="M 530 440 L 650 490" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" strokeDasharray="5,5" />
      <text x="570" y="475" fontSize="10" fill="#1f2937" fontWeight="700">Retrieve Keys</text>
      <path d="M 350 460 L 250 460" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="290" y="475" fontSize="10" fill="#b8874f" fontWeight="700">Send Reply SMS</text>
      <path d="M 1090 260 L 1090 280" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="1100" y="275" fontSize="10" fill="#b8874f" fontWeight="700">Cache</text>
      <path d="M 1090 350 L 1090 370" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="1100" y="365" fontSize="10" fill="#b8874f" fontWeight="700">Serve</text>
      <path d="M 530 150 Q 760 80 1010 150" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" strokeDasharray="5,5" />
      <text x="750" y="70" fontSize="10" fill="#1f2937" fontWeight="700">API for Web Chatbot</text>

      <circle cx="50" cy="680" r="4" fill="#b8874f" />
      <text x="65" y="684" fontSize="11" fill="#4b5563">Twilio Flow</text>
      <circle cx="250" cy="680" r="4" fill="#1f2937" />
      <text x="265" y="684" fontSize="11" fill="#4b5563">AWS Internal Flow</text>
      <line x1="480" y1="680" x2="510" y2="680" stroke="#1f2937" strokeWidth="2" strokeDasharray="5,5" />
      <text x="520" y="684" fontSize="11" fill="#4b5563">Async/Lookup Flow</text>
    </svg>
  </div>
);

const ServicesChatbots = () => {
  return (
    <div className="services-placeholder-page chatbot-service-page">
      <ServicesSubMenu />

      <section className="chatbot-hero" aria-labelledby="chatbot-service-title">
        <div className="chatbot-hero-copy">
          <p className="services-placeholder-kicker">AI Customer Support</p>
          <h1 id="chatbot-service-title">AI Integrated Intelligent Chatbots</h1>
          <p>
            Hogcloud builds website and SMS chatbot systems that answer repetitive
            customer questions, reduce support load, and keep your team focused on the
            conversations that actually need a person.
          </p>
          <div className="chatbot-hero-actions">
            <Link className="chatbot-primary-link" to="/contact">Request a Demo</Link>
            <Link className="chatbot-secondary-link" to="/services/websites">Back to Website Services</Link>
          </div>
        </div>
        <img src={robotImage} alt="Hogcloud AI chatbot service" />
      </section>

      <section className="chatbot-outcomes" aria-label="Chatbot service outcomes">
        {outcomes.map((outcome) => (
          <article key={outcome.label}>
            <strong>{outcome.value}</strong>
            <span>{outcome.label}</span>
          </article>
        ))}
      </section>

      <section className="chatbot-section chatbot-two-column" aria-labelledby="chatbot-problem-title">
        <div>
          <p className="services-placeholder-kicker">The Problem</p>
          <h2 id="chatbot-problem-title">Your team should not have to answer the same questions all day.</h2>
        </div>
        <div className="chatbot-copy-stack">
          <p>
            Many businesses lose time to the same support questions over email, phone,
            website forms, and text messages. Customers want fast answers about hours,
            pricing, scheduling, order status, returns, policies, and account access.
          </p>
          <p>
            A well-built chatbot does not replace your team. It filters the repetitive
            work, answers the known questions, and gives staff more time for complex
            customer needs.
          </p>
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-features-title">
        <p className="services-placeholder-kicker">What Hogcloud Builds</p>
        <h2 id="chatbot-features-title">A practical support system for web and SMS.</h2>
        <div className="chatbot-feature-grid">
          {features.map((feature) => (
            <article key={feature}>
              <p>{feature}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-workflow-title">
        <p className="services-placeholder-kicker">How It Works</p>
        <h2 id="chatbot-workflow-title">From business knowledge to customer answers.</h2>
        <div className="chatbot-workflow">
          {workflow.map((step, index) => (
            <article key={step.title}>
              <span>{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-industries-title">
        <p className="services-placeholder-kicker">Use Cases</p>
        <h2 id="chatbot-industries-title">Useful across customer-heavy businesses.</h2>
        <div className="chatbot-use-case-grid">
          {useCases.map((useCase) => (
            <article key={useCase.title}>
              <h3>{useCase.title}</h3>
              <p>{useCase.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section chatbot-two-column chatbot-architecture" aria-labelledby="chatbot-architecture-title">
        <div>
          <p className="services-placeholder-kicker">Technology</p>
          <h2 id="chatbot-architecture-title">Built on cloud services that are meant to operate.</h2>
        </div>
        <div className="chatbot-copy-stack">
          <p>
            Hogcloud can design the system around AWS services such as Lambda,
            DynamoDB, S3, CloudFront, Secrets Manager, and managed AI model access,
            with Twilio available when SMS is part of the workflow.
          </p>
          <p>
            The result is a service that can start small, stay maintainable, and grow
            into admin dashboards, reporting, broadcast messaging, or deeper customer
            account integrations.
          </p>
        </div>
        <ArchitectureDiagram />
      </section>

      <section className="chatbot-cta" aria-labelledby="chatbot-cta-title">
        <h2 id="chatbot-cta-title">Ready to make customer questions easier to handle?</h2>
        <p>
          Hogcloud can review your current FAQ workload and map the first chatbot or SMS
          support workflow that would give your team time back.
        </p>
        <Link className="chatbot-primary-link" to="/contact">Start the Conversation</Link>
      </section>
    </div>
  );
};

export default ServicesChatbots;
